import React, { useState, useEffect } from 'react';
import { Calendar, Users, Mail, CheckCircle, XCircle, Clock, Search, Play, Pause, TrendingUp, Activity, Plus, RefreshCw, GitBranch } from 'lucide-react';
import { A2AClient } from './lib/a2a-client';
import { createClient } from '@supabase/supabase-js';
import { UXProvider } from './contexts/UXContext';
import ResponsiveCandidateView from './components/ResponsiveCandidateView';
import DeviceIndicator from './components/DeviceIndicator';
import VisualFlowTab from './components/VisualFlowTab';
import { 
  createProcessManager, 
  createWorkflowOrchestrator,
  ProcessPhase,
  ProcessState 
} from './shared/a2a-process-manager';
import { ProcessStatusPanel } from './shared/components/ProcessStatusPanel';
import './App.css';

// Initialize Supabase
const supabase = createClient(
  'https://uszhqmcohbwnabxgfuhz.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVzemhxbWNvaGJ3bmFieGdmdWh6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk4NDMzMjYsImV4cCI6MjA3NTQxOTMyNn0.YNvc18MX9tchryD6N3ImzFMh3EGybG3fGkZnIuL6AWc'
);

// Initialize A2A Client
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://a2ahrrecruit-server.appsokay.net';

const HRRecruitmentDashboard = () => {
  // State management
  const [activeTab, setActiveTab] = useState('dashboard');
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [stats, setStats] = useState({
    sourced: 0,
    screened: 0,
    scheduled: 0,
    rejected: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // A2A Client instance
  const [a2aClient] = useState(() => new A2AClient(
    BACKEND_URL,
    'hr-client-001',
    { enableLogging: true }
  ));

  // Process Management
  const [processStatus, setProcessStatus] = useState(null);
  const [processManager] = useState(() => createProcessManager({
    onStatusUpdate: (status) => {
      setProcessStatus(status);
    },
    onPhaseChange: (phase) => {
      console.log('Phase changed:', phase.name);
    },
    onComplete: (result) => {
      console.log('Process completed:', result);
      loadCandidates();
    },
    onError: (error) => {
      console.error('Process error:', error);
      setError(error.error);
    }
  }));

  // Initialize: Discover capabilities and load data
  useEffect(() => {
    initializeApp();
    
    // Cleanup on unmount
    return () => {
      a2aClient.cleanup();
    };
  }, []);

  // Real-time subscriptions for candidates
  useEffect(() => {
    const subscription = supabase
      .channel('candidates_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'hr_candidates'
      }, (payload) => {
        console.log('Candidate changed:', payload);
        loadCandidates();
        updateStats();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const initializeApp = async () => {
    setLoading(true);
    try {
      // Task 1: Discover capabilities
      console.log('Discovering agent capabilities...');
      const caps = await a2aClient.discoverCapabilities();
      setCapabilities(caps);
      console.log(`Discovered ${caps.length} capabilities`);

      // Load initial data
      await Promise.all([
        loadJobs(),
        loadCandidates()
      ]);

      console.log('Dashboard initialized successfully');
    } catch (err) {
      setError(`Initialization failed: ${err.message}`);
      console.error('Initialization error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('hr_jobs')
        .select('*')
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setJobs(data || []);
      if (data && data.length > 0 && !selectedJob) {
        setSelectedJob(data[0]);
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
    }
  };

  const loadCandidates = async () => {
    try {
      const { data, error } = await supabase
        .from('hr_candidates')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      
      setCandidates(data || []);
      updateStats(data || []);
    } catch (err) {
      console.error('Error loading candidates:', err);
    }
  };

  const updateStats = (candidatesList = candidates) => {
    const sourced = candidatesList.filter(c => c.current_status === 'sourced').length;
    const screened = candidatesList.filter(c => c.current_status === 'screened').length;
    const scheduled = candidatesList.filter(c => c.current_status === 'scheduled').length;
    const rejected = candidatesList.filter(c => c.current_status === 'rejected').length;
    
    setStats({ sourced, screened, scheduled, rejected });
  };

  // Full recruitment workflow with 4-phase process management
  const startRecruitmentWorkflow = async () => {
    if (!selectedJob) {
      setError('Please select a job first');
      return;
    }

    try {
      processManager.start();
      setError(null);

      const orchestrator = createWorkflowOrchestrator(a2aClient, processManager);

      // PHASE 1: DISCOVERY (0-25%)
      await orchestrator.executePhase(
        ProcessPhase.PHASE_1_DISCOVERY,
        [
          async (orch) => {
            orch.processManager.addMessage('System', `Preparing recruitment for: ${selectedJob.title}`, 'process');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            orch.processManager.addMessage('Discovery Agent', 'Verifying job requirements and skills', 'process');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            orch.processManager.addMessage('Discovery Agent', '✓ Job requirements validated', 'success');
          }
        ],
        { startProgress: 0, progressRange: 25 }
      );

      // PHASE 2: EXECUTION (25-65%)
      await orchestrator.executePhase(
        ProcessPhase.PHASE_2_EXECUTION,
        [
          async (orch) => {
            // Source candidates
            const sourceResult = await orch.executeTask('source_candidates', {
              job_id: selectedJob.id,
              sources: ['LinkedIn', 'Indeed', 'GitHub'],
              count: 5
            }, {
              agent: 'Sourcing Agent',
              successMessage: (result) => `✓ Found ${result.sourced_count} candidates matching criteria`,
              recoverable: false
            });

            if (!sourceResult) {
              throw new Error('Failed to source candidates');
            }

            await loadCandidates();
            await orch.processManager.wait(800);

            // Screen each candidate
            if (sourceResult.candidate_ids && sourceResult.candidate_ids.length > 0) {
              const totalCandidates = sourceResult.candidate_ids.length;
              
              for (let i = 0; i < sourceResult.candidate_ids.length; i++) {
                if (!orch.processManager.shouldContinue()) break;

                const candidateId = sourceResult.candidate_ids[i];
                const candidate = (await supabase.from('hr_candidates').select('*').eq('id', candidateId).single()).data;
                
                if (!candidate) continue;

                // Update progress within phase
                const candidateProgress = 30 + (i / totalCandidates) * 20;
                orch.processManager.setProgress(candidateProgress);

                const screenResult = await orch.executeTask('screen_candidate', {
                  candidate_id: candidateId,
                  job_id: selectedJob.id
                }, {
                  agent: 'Screening Agent',
                  successMessage: (result) => 
                    `✓ ${candidate.name}: Score ${result.match_score}/100 - ${result.recommendation === 'interview' ? 'Passed' : 'Not suitable'}`,
                  recoverable: true
                });

                if (screenResult && screenResult.recommendation === 'interview') {
                  // Schedule interview
                  const scheduleResult = await orch.executeTask('schedule_interview', {
                    candidate_id: candidateId,
                    job_id: selectedJob.id,
                    interviewer: 'Sarah Johnson',
                    duration_minutes: 60
                  }, {
                    agent: 'Scheduling Agent',
                    successMessage: (result) => 
                      `✓ Interview scheduled for ${candidate.name} at ${new Date(result.scheduled_time).toLocaleString()}`,
                    recoverable: true
                  });

                  if (scheduleResult) {
                    // Generate email
                    await orch.executeTask('generate_email', {
                      candidate_id: candidateId,
                      email_type: 'interview_confirmation',
                      additional_context: {
                        interview_time: scheduleResult.scheduled_time,
                        interviewer: scheduleResult.interviewer
                      }
                    }, {
                      agent: 'Communication Agent',
                      successMessage: (result) => `✓ Email ready: "${result.subject}" for ${candidate.name}`,
                      recoverable: true
                    });
                  }
                }

                await orch.processManager.wait(600);
              }
            }
          }
        ],
        { startProgress: 25, progressRange: 40, delayBetweenTasks: false }
      );

      // PHASE 3: VALIDATION (65-85%)
      await orchestrator.executePhase(
        ProcessPhase.PHASE_3_VALIDATION,
        [
          async (orch) => {
            orch.processManager.addMessage('Validation Agent', 'Verifying all candidate records', 'process');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            await loadCandidates();
            orch.processManager.addMessage('Validation Agent', '✓ All records verified successfully', 'success');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            orch.processManager.addMessage('Quality Agent', 'Checking data quality and completeness', 'process');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            orch.processManager.addMessage('Quality Agent', '✓ Quality checks passed', 'success');
          }
        ],
        { startProgress: 65, progressRange: 20 }
      );

      // PHASE 4: COMPLETION (85-100%)
      await orchestrator.executePhase(
        ProcessPhase.PHASE_4_COMPLETION,
        [
          async (orch) => {
            orch.processManager.addMessage('Storage Agent', 'Saving all results to database', 'process');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            await loadCandidates();
            updateStats();
            orch.processManager.addMessage('Storage Agent', '✓ Results saved successfully', 'success');
            await orch.processManager.wait(500);
          },
          async (orch) => {
            orch.processManager.addMessage('Coordinator Agent', '🎉 Recruitment workflow completed successfully!', 'success');
            orch.processManager.addMessage('Summary', `Processed ${stats.sourced} candidates, ${stats.scheduled} interviews scheduled`, 'info');
          }
        ],
        { startProgress: 85, progressRange: 15 }
      );

      processManager.complete({
        totalCandidates: stats.sourced,
        scheduled: stats.scheduled,
        screened: stats.screened
      });

    } catch (err) {
      processManager.addError('System', err, false);
      setError(`Workflow failed: ${err.message}`);
    }
  };

  // Individual capability actions
  const sourceCandidates = async () => {
    if (!selectedJob) {
      setError('Please select a job first');
      return;
    }

    setLoading(true);
    try {
      const result = await a2aClient.executeTask('source_candidates', {
        job_id: selectedJob.id,
        sources: ['LinkedIn', 'Indeed', 'GitHub'],
        count: 10
      });

      await loadCandidates();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const screenCandidate = async (candidateId) => {
    if (!selectedJob) {
      setError('Please select a job first');
      return;
    }

    setLoading(true);
    try {
      await a2aClient.executeTask('screen_candidate', {
        candidate_id: candidateId,
        job_id: selectedJob.id
      });

      await loadCandidates();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const scheduleInterview = async (candidateId) => {
    if (!selectedJob) {
      setError('Please select a job first');
      return;
    }

    setLoading(true);
    try {
      await a2aClient.executeTask('schedule_interview', {
        candidate_id: candidateId,
        job_id: selectedJob.id,
        interviewer: 'Sarah Johnson'
      });

      await loadCandidates();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'sourced': return 'bg-blue-100 text-blue-800';
      case 'screened': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'sourced': return <Search className="w-4 h-4" />;
      case 'screened': return <CheckCircle className="w-4 h-4" />;
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };


  // Render dashboard wrapped in UX Provider
  return (
    <UXProvider a2aClient={a2aClient}>
      <div className="min-h-screen bg-gray-50">
      {/* Device Indicator (Task 4: UX Negotiation) */}
      <DeviceIndicator />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-600 p-2 rounded-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">HR Recruitment AI System</h1>
                <p className="text-sm text-gray-500">A2A Multi-Agent Candidate Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Job Selector */}
          {jobs.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Active Job Position:
              </label>
              <select
                value={selectedJob?.id || ''}
                onChange={(e) => setSelectedJob(jobs.find(j => j.id === e.target.value))}
                className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.title} ({job.requirements?.join(', ')})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: null },
              { id: 'workflow', label: 'Workflow', icon: null },
              { id: 'visual', label: 'Visual Flow', icon: <GitBranch className="w-4 h-4 inline mr-1" /> },
              { id: 'candidates', label: 'Candidates', icon: null },
              { id: 'capabilities', label: 'Capabilities', icon: null }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Sourced</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.sourced}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Search className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Screened</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.screened}</p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Scheduled</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.scheduled}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Rejected</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.rejected}</p>
                  </div>
                  <div className="bg-red-100 p-3 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Pipeline Overview */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recruitment Pipeline</h2>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Search className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Sourcing</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${stats.sourced > 0 ? '100' : '0'}%` }}
                    />
                  </div>
                </div>
                
                <div className="mx-4 text-gray-400">→</div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium text-gray-700">Screening</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${stats.sourced > 0 ? ((stats.screened + stats.scheduled) / stats.sourced * 100) : 0}%` }}
                    />
                  </div>
                </div>
                
                <div className="mx-4 text-gray-400">→</div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">Scheduling</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(stats.screened + stats.scheduled) > 0 ? (stats.scheduled / (stats.screened + stats.scheduled) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={sourceCandidates}
                  disabled={loading || !selectedJob}
                  className="flex items-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Search className="w-6 h-6 text-indigo-600" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900">Source Candidates</div>
                    <div className="text-sm text-gray-500">Find new candidates</div>
                  </div>
                </button>

                <button
                  onClick={() => loadCandidates()}
                  disabled={loading}
                  className="flex items-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="w-6 h-6 text-indigo-600" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900">Refresh Data</div>
                    <div className="text-sm text-gray-500">Reload from database</div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveTab('workflow')}
                  className="flex items-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
                >
                  <Activity className="w-6 h-6 text-purple-600" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900">Workflow Control</div>
                    <div className="text-sm text-gray-500">Manage process</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'candidates' && (
          <ResponsiveCandidateView
            candidates={candidates}
            loading={loading}
            onScreen={screenCandidate}
            onSchedule={scheduleInterview}
          />
        )}

        {activeTab === 'capabilities' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Available Agent Capabilities</h2>
              <p className="text-sm text-gray-500 mt-1">Discovered from remote agent</p>
            </div>
            <div className="p-6">
              {capabilities.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Loading capabilities...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {capabilities.map((cap, idx) => {
                    // Safely get parameters as array
                    const params = Array.isArray(cap.parameters) 
                      ? cap.parameters 
                      : (typeof cap.parameters === 'object' && cap.parameters 
                          ? Object.keys(cap.parameters) 
                          : []);
                    
                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-gray-900">{cap.name || 'Unknown'}</h3>
                            <p className="text-sm text-gray-600 mt-1">{cap.description || 'No description'}</p>
                            {params.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                <div className="text-xs text-gray-500">
                                  <span className="font-medium">Parameters:</span> {params.join(', ')}
                                </div>
                              </div>
                            )}
                            {cap.returns && (
                              <div className="mt-1 text-xs text-gray-500">
                                <span className="font-medium">Returns:</span> {cap.returns}
                              </div>
                            )}
                            {cap.responseSchema && (
                              <div className="mt-2 text-xs text-indigo-600">
                                <span className="font-medium">✓ Response schema learned</span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Available
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'visual' && (
          <VisualFlowTab
            a2aClient={a2aClient}
            isRunning={loading}
            setIsRunning={setLoading}
          />
        )}

        {activeTab === 'workflow' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">A2A Workflow Control Center</h2>
              <p className="text-gray-600">
                Control and monitor the 4-phase recruitment process with real-time status updates
              </p>
            </div>
            
            {processStatus ? (
              <ProcessStatusPanel
                status={processStatus}
                onStart={startRecruitmentWorkflow}
                onStop={() => processManager.stop()}
                onPause={() => processManager.pause()}
                onResume={() => processManager.resume()}
                onReset={() => processManager.reset()}
                showControls={true}
                showPhases={true}
                showMessages={true}
                maxMessagesHeight="500px"
              />
            ) : (
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Start</h3>
                <p className="text-gray-600 mb-6">
                  Click "Start Process" to begin the recruitment workflow
                </p>
                <button
                  onClick={startRecruitmentWorkflow}
                  disabled={!selectedJob}
                  className={`inline-flex items-center space-x-2 px-8 py-4 rounded-lg font-medium transition-colors ${
                    !selectedJob
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  <Play className="w-5 h-5" />
                  <span>Start Process</span>
                </button>
                {!selectedJob && (
                  <p className="text-sm text-red-600 mt-4">Please select a job position first</p>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
    </UXProvider>
  );
};

export default HRRecruitmentDashboard;
